/**
 * The mission map (P5-UX-01, the founder's look A, 2026-11-04): a game's missions as stops on one
 * winding trail, cut into chapters, with your bean on the next one. A track game's tracks (ADR
 * 0027: a quiz category, a picture theme) are its chapters; a game with one line has it cut into
 * fours. Battle, the missions that open after a win and the Pass's sit beside the trail, not on it.
 * Pure: solo and the room's lobby say what's open, and this lays it out.
 */

/** A mission as the map needs it: the manifest's place for it, and what the caller knows of it. */
export interface MapMission {
  id: string;
  name: string;
  pack?: string | undefined;
  opensAfter?: string | undefined;
  openFromStart?: boolean | undefined;
  track?: string | undefined;
  open: boolean;
  /** Best stars so far (undefined: never won). */
  stars: number | undefined;
  /** Why it's locked, in words ("" when open). */
  why: string;
}

export interface Stop extends MapMission {
  /** Where you are: the first open mission not yet won, on the trail. */
  current: boolean;
}

export interface Chapter {
  name: string;
  stops: Stop[];
  /** How many of its stops are won. */
  won: number;
  done: boolean;
}

export interface MissionMap {
  chapters: Chapter[];
  /** Battle, the missions that open after a win, and the Pass's: beside the trail. */
  beside: Stop[];
  /** The stop your bean stands on, if any is left to win. */
  current: string | null;
}

/** A line's chapter: four missions, about a sitting's worth. */
export const CHAPTER_SIZE = 4;

const won = (m: MapMission) => m.stars !== undefined;

export function mapOf(missions: readonly MapMission[]): MissionMap {
  const onTrail = (m: MapMission) => !m.pack && !m.opensAfter && m.openFromStart !== true;
  const groups: { name: string; ms: MapMission[] }[] = [];
  const line = missions.filter((m) => onTrail(m) && m.track === undefined);
  for (let i = 0; i < line.length; i += CHAPTER_SIZE)
    groups.push({
      name: `Chapter ${String(i / CHAPTER_SIZE + 1)}`,
      ms: line.slice(i, i + CHAPTER_SIZE),
    });
  for (const m of missions.filter((x) => onTrail(x) && x.track !== undefined)) {
    const group = groups.find((g) => g.name === m.track);
    if (group) group.ms.push(m);
    else groups.push({ name: m.track ?? "", ms: [m] });
  }
  const current = groups.flatMap((g) => g.ms).find((m) => m.open && !won(m))?.id ?? null;
  const stop = (m: MapMission): Stop => ({ ...m, current: m.id === current });
  const chapters = groups.map(({ name, ms }) => {
    const n = ms.filter(won).length;
    return { name, stops: ms.map(stop), won: n, done: n === ms.length };
  });
  const beside = missions.filter((m) => !onTrail(m)).map(stop);
  return { chapters, beside, current };
}

/**
 * Where a stop sits across the trail, from -1 (left) to 1 (right): a gentle wave, the same for a
 * stop whatever else is folded, so the trail doesn't jump when a chapter opens.
 */
export const sway = (index: number) => Math.round(Math.sin(index * 1.1) * 100) / 100;
